import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router';
import messageImage from '@/assets/images/message.webp';
import { useUserContext } from '@/auth/UserContext';
import { Button } from '@/components/Button';
import { Phone } from '@/components/Icons/Phone';
import { ConfirmModal } from '@/components/Modal/ConfirmModal';
import { ProfileMenu } from '@/components/ProfileMenu';
import { Snackbar } from '@/components/Snackbar';
import { useSubscribeToSms } from '@/pages/Subscription/hooks/useSubscribeToSms';
import { ROUTE_HOME } from '@/router/routes';
import {
  BorderRadius,
  ButtonVariant,
  Size,
  SnackbarVariant,
} from '@/types/components';

export const SmsPayment: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { user } = useUserContext();
  const [isProfileMenuOpen, setIsProfileMenuOpen] = useState(false);
  const [confirmRecurring, setConfirmRecurring] = useState<boolean | null>(
    null,
  );
  const [isErrorSnackbarOpen, setIsErrorSnackbarOpen] = useState(false);

  const hasPhoneNumber = Boolean(user?.phoneNumber);
  const phoneNumberText = hasPhoneNumber
    ? user?.phoneNumber
    : t('subscription.checkout.smsNoPhone');
  const confirmDescription = t(
    confirmRecurring
      ? 'subscription.checkout.smsConfirmRecurring'
      : 'subscription.checkout.smsConfirmOneTime',
    { phone: user?.phoneNumber },
  );

  const { subscribeToSms, isPending } = useSubscribeToSms({
    onSuccess: () => {
      setConfirmRecurring(null);
      navigate(ROUTE_HOME, {
        replace: true,
        state: { subscriptionSuccess: true },
      });
    },
    onError: () => {
      setConfirmRecurring(null);
      setIsErrorSnackbarOpen(true);
    },
  });

  const confirmSubscription = () => {
    if (confirmRecurring === null) {
      return;
    }

    subscribeToSms(confirmRecurring);
  };

  return (
    <div className="flex h-full flex-col justify-between gap-6 rounded-2xl bg-card p-6 shadow-card">
      <div className="flex flex-col items-end gap-2">
        <p className="w-full text-[18px]/[24px] font-semibold text-foreground md:text-[20px]/[28px] md:font-medium">
          {t('subscription.checkout.smsTitle')}
        </p>
        <div className="flex w-full items-start gap-3 rounded-xl bg-card p-3 shadow-card">
          <span className="flex size-8 shrink-0 items-center justify-center rounded-full bg-[#EDF2FD]">
            <Phone className="size-4 text-primary" />
          </span>
          <div className="flex min-w-0 flex-col gap-1">
            <p className="text-subhead-14 text-foreground">
              {t('subscription.checkout.smsPhoneLabel')}
            </p>
            <p className="text-body-14 text-muted-foreground">
              {phoneNumberText}
            </p>
          </div>
        </div>
        {!hasPhoneNumber && (
          <button
            type="button"
            onClick={() => setIsProfileMenuOpen(true)}
            className="cursor-pointer px-1 text-subhead-14 text-primary"
          >
            {t('subscription.checkout.smsAddPhone')}
          </button>
        )}
      </div>
      <div className="flex flex-col gap-4">
        <div className="flex flex-col gap-2">
          <Button
            variant={ButtonVariant.Secondary}
            size={Size.Large}
            radius={BorderRadius.Pill}
            disabled={!hasPhoneNumber}
            className="w-full md:rounded-xl"
            onClick={() => setConfirmRecurring(false)}
          >
            {t('subscription.checkout.smsOneTimePayment')}
          </Button>
          <Button
            variant={ButtonVariant.Primary}
            size={Size.Large}
            radius={BorderRadius.Pill}
            disabled={!hasPhoneNumber}
            className="w-full md:rounded-xl"
            onClick={() => setConfirmRecurring(true)}
          >
            {t('subscription.checkout.smsRecurringPayment')}
          </Button>
        </div>
        <p className="text-center text-body-14 text-subtle-foreground md:text-left">
          {t('subscription.checkout.smsRecurringNote')}
        </p>
      </div>

      <ConfirmModal
        simpleView
        isOpen={confirmRecurring !== null}
        title={t('subscription.checkout.smsConfirmTitle')}
        description={confirmDescription}
        image={messageImage}
        isPending={isPending}
        onClose={() => setConfirmRecurring(null)}
        onConfirm={confirmSubscription}
      />

      <ProfileMenu
        isOpen={isProfileMenuOpen}
        onClose={() => setIsProfileMenuOpen(false)}
      />

      <Snackbar
        compact
        isOpen={isErrorSnackbarOpen}
        message={t('global.errorOccurred')}
        variant={SnackbarVariant.Error}
        onDismiss={() => setIsErrorSnackbarOpen(false)}
      />
    </div>
  );
};
